export interface TasksConfig {
  maxVisible?: number;
  maxPerDay?: number;
  [key: string]: unknown;
}

export interface AppConfig {
  api_url: string;
  env: string;
  tasks?: TasksConfig;
  [key: string]: unknown;
}

interface ConfigLoaders {
  loadDev: () => Promise<AppConfig>;
  loadStage: () => Promise<AppConfig>;
  loadProd: () => Promise<AppConfig>;
}

interface CreateGetConfigParams {
  getMode: () => string;
  loaders: ConfigLoaders;
  logError?: (message: string, error: unknown) => void;
}

const DEFAULT_TASKS_LIMIT = 40;

const withTaskDefaults = (config: AppConfig): AppConfig => {
  const tasks = config.tasks || {};

  return {
    ...config,
    tasks: {
      ...tasks,
      maxVisible:
        typeof tasks.maxVisible === 'number'
          ? tasks.maxVisible
          : DEFAULT_TASKS_LIMIT,
      maxPerDay:
        typeof tasks.maxPerDay === 'number'
          ? tasks.maxPerDay
          : DEFAULT_TASKS_LIMIT,
    },
  };
};

export const createGetConfig = ({
  getMode,
  loaders,
  logError,
}: CreateGetConfigParams) => {
  const cache: Record<string, AppConfig> = {};

  return async (): Promise<AppConfig> => {
    const mode = getMode();

    if (cache[mode]) {
      return cache[mode];
    }

    try {
      let config: AppConfig;

      if (mode === 'development') {
        config = await loaders.loadDev();
      } else if (mode === 'staging') {
        config = await loaders.loadStage();
      } else {
        config = await loaders.loadProd();
      }

      const merged = withTaskDefaults(config);
      cache[mode] = merged;

      return merged;
    } catch (error) {
      logError?.('Failed to load config:', error);
      const devConfig = await loaders.loadDev();
      const merged = withTaskDefaults(devConfig);
      cache[mode] = merged;

      return merged;
    }
  };
};
